import { useEffect, useMemo, useState } from 'react'
import type { Key } from 'react'
import { SearchOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { Button, CheckboxGroup, Input, Modal, Table, Tag, message } from '@/components/ui'

export interface ApprovedRegistrant {
  id: number
  full_name: string
  email: string
  contact_number: string | null
  answers?: Record<string, any>
}

interface FilterFieldOption {
  id: number
  label: string
  value: string
}

interface FilterFieldGroup {
  fieldId: number
  label: string
  identifier: string
  options: FilterFieldOption[]
}

interface AddAttendeesModalProps {
  open: boolean
  onClose: () => void
  onAdd: (registrantIds: number[]) => void
  registrants: ApprovedRegistrant[]
  existingRegistrantIds: number[]
  filterOptions: {
    fields: FilterFieldGroup[]
  }
  loading?: boolean
}

const AddAttendeesModal = ({
  open,
  onClose,
  onAdd,
  registrants,
  existingRegistrantIds,
  filterOptions,
  loading = false,
}: AddAttendeesModalProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const [fieldSelections, setFieldSelections] = useState<Record<string, string[]>>({})

  const existingIds = useMemo(() => new Set(existingRegistrantIds), [existingRegistrantIds])

  const matchesFieldFilters = (registrant: ApprovedRegistrant) => {
    const answers = registrant.answers || {}
    return Object.entries(fieldSelections).every(([identifier, selectedValues]) => {
      if (!selectedValues.length) return true
      const answer = answers[identifier]
      if (Array.isArray(answer)) {
        return answer.some((val) => selectedValues.includes(String(val)))
      }
      if (answer == null) return false
      return selectedValues.includes(String(answer))
    })
  }

  const filteredRegistrants = useMemo(() => {
    let base = registrants
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase()
      base = registrants.filter((registrant) => {
        return (
          registrant.full_name?.toLowerCase().includes(search) ||
          registrant.email?.toLowerCase().includes(search) ||
          registrant.contact_number?.toLowerCase().includes(search)
        )
      })
    }

    return base.filter(matchesFieldFilters)
  }, [registrants, searchValue, fieldSelections])

  useEffect(() => {
    if (!open) {
      setSearchValue('')
      setSelectedRowKeys([])
      setFieldSelections({})
    }
  }, [open])

  const handleAdd = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select at least one attendee')
      return
    }
    onAdd(selectedRowKeys.map((key) => Number(key)))
  }

  const columns: ColumnsType<ApprovedRegistrant> = [
    {
      title: 'Full Name',
      dataIndex: 'full_name',
      key: 'full_name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Contact Number',
      dataIndex: 'contact_number',
      key: 'contact_number',
      render: (value: string | null) => value || '-',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) =>
        existingIds.has(record.id) ? (
          <Tag color="blue">Already added</Tag>
        ) : (
          <Tag color="green">Approved</Tag>
        ),
    },
  ]

  return (
    <Modal
      title="Add Attendees"
      open={open}
      onClose={onClose}
      footer={null}
      width={1000}
      centered
    >
      <div className="pt-2">
        <p className="text-sm text-black/45 mb-4">
          Search approved registrants and filter by form fields before adding them to this scan point.
        </p>

        <div className="flex gap-6">
          {/* Left Panel - Filters */}
          <div className="flex w-[360px] flex-col rounded bg-white shadow-sm overflow-hidden border border-[#F0F0F0]">
            <div className="px-5 py-4 border-b border-[#F0F0F0] text-sm font-medium text-black/85">
              Filters
            </div>
            <div className="flex flex-col gap-4 p-5 overflow-y-auto" style={{ maxHeight: '560px' }}>
              <Input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search by name, email, or phone"
                suffix={<SearchOutlined className="text-black/45" />}
              />

              <div className="flex items-center justify-between">
                <span className="text-sm text-black/65">
                  Selected: <span className="font-semibold">{selectedRowKeys.length}</span>
                </span>
                <button
                  type="button"
                  onClick={() => setFieldSelections({})}
                  className="text-sm font-semibold text-[#4277BB] hover:text-[#4277BB]/80 bg-transparent border-none cursor-pointer"
                >
                  Clear Filters
                </button>
              </div>

              {filterOptions.fields.length === 0 ? (
                <div className="text-sm text-black/45">No filterable fields configured.</div>
              ) : (
                filterOptions.fields.map((field, idx) => (
                  <div key={field.fieldId} className="flex flex-col gap-2">
                    <div className="pb-1 text-sm text-black/85">{field.label}</div>
                    <CheckboxGroup
                      options={field.options.map((opt) => ({
                        label: opt.label,
                        value: opt.value,
                      }))}
                      value={fieldSelections[field.identifier] || []}
                      onChange={(checked) =>
                        setFieldSelections((prev) => ({
                          ...prev,
                          [field.identifier]: checked as string[],
                        }))
                      }
                      className="flex flex-col gap-2"
                      size="small"
                    />
                    {idx < filterOptions.fields.length - 1 ? (
                      <div className="h-px bg-[#EBEBEB]" />
                    ) : null}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Attendees Table */}
          <div className="flex-1">
            <div className="mb-3 text-sm text-black/65">
              Showing {filteredRegistrants.length} of {registrants.length} approved attendees
            </div>

            <Table<ApprovedRegistrant>
              columns={columns}
              dataSource={filteredRegistrants}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showTotal: (total) => `Total ${total} items`,
                showSizeChanger: false,
              }}
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                getCheckboxProps: (record) => ({
                  disabled: existingIds.has(record.id),
                }),
              }}
            />

            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-black/60">
                Selected: <span className="font-semibold">{selectedRowKeys.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="secondary" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAdd}
                  loading={loading}
                  disabled={selectedRowKeys.length === 0}
                >
                  Add Attendees
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AddAttendeesModal
